import { Modal } from '../../ui/Modal';
import { Button } from '../../ui/Button';
import { Text } from '../../ui/Text';
import { token } from '../../ui/theme';

/*
 * PausedModal — shown while the table is paused because a seat is "reserved":
 * either a player left (awaiting the host's replace-or-wait decision) or a
 * player disconnected (reserved during the reconnect grace period). The host
 * (game creator) can drop a computer into any reserved seat immediately, or keep
 * waiting. Everyone else sees a passive notice. Modeled on GameOverModal.
 */

export interface PausedSeat {
  /** Real server seat index (from table.seatIndexes), for the fill request. */
  seatIndex: number;
  /** The player's name, so the host knows which seat this is. */
  name: string;
  /** Why the seat is reserved: they left, or they dropped and may reconnect. */
  reason: 'left' | 'disconnected';
}

interface Props {
  open: boolean;
  isCreator: boolean;
  pausedSeats: PausedSeat[];
  busy?: boolean;
  onFillWithComputer: (seatIndex: number) => void;
  /** Host dismisses the prompt to keep waiting. */
  onWait: () => void;
}

const statusLabel = (reason: PausedSeat['reason']) =>
  reason === 'disconnected' ? 'reconnecting…' : 'left the game';

export const PausedModal = ({
  open,
  isCreator,
  pausedSeats,
  busy,
  onFillWithComputer,
  onWait,
}: Props) => {
  const anyDisconnected = pausedSeats.some((s) => s.reason === 'disconnected');

  if (!isCreator) {
    return (
      <Modal open={open} title="Game paused">
        <Text variant="muted">
          {anyDisconnected
            ? 'A player lost connection. Waiting for them to reconnect…'
            : 'A player left. Waiting for the host to fill the seat or for a new player to join…'}
        </Text>
      </Modal>
    );
  }

  return (
    <Modal
      open={open}
      title="Game paused"
      actions={
        <Button variant="ghost" onClick={onWait} disabled={busy}>
          Keep waiting
        </Button>
      }
    >
      <Text variant="muted" style={{ marginBottom: 'var(--space-4)' }}>
        {anyDisconnected
          ? 'A player lost connection — their seat is reserved briefly so they can rejoin. Replace them with a computer now, or keep waiting.'
          : 'Replace them with a computer, or keep waiting — the table stays open in the lobby for a new player to take the seat.'}
      </Text>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {pausedSeats.map((seat) => (
          <div
            key={seat.seatIndex}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--space-3)',
              background: token('color-surface'),
              border: `1px solid ${token('color-border')}`,
              borderRadius: 8,
              padding: '10px 14px',
            }}
          >
            <span style={{ minWidth: 0 }}>
              <Text variant="value" as="div">
                {seat.name}
              </Text>
              <Text variant="muted" as="div" style={{ fontSize: '0.75rem' }}>
                {statusLabel(seat.reason)}
              </Text>
            </span>
            <Button
              variant="primary"
              size="sm"
              onClick={() => onFillWithComputer(seat.seatIndex)}
              disabled={busy}
            >
              Replace with computer
            </Button>
          </div>
        ))}
      </div>
    </Modal>
  );
};
